/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { CIColors, ProjectRole } from 'shared';

import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { QueryLoader } from '../../../../components/QueryLoader';
import { SelectWithDescription } from '../../../../components/SelectWithDescription/SelectWithDescription';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { ProjectUserApiService } from '../../../../services/ProjectUserApiService';
import { UserApiService } from '../../../../services/UserApiService';
import { fetchOrganizationUsers, fetchProject, fetchTeams } from '../../../../utils/queryFns';
import { useProjectRolesI18n } from '../../../../utils/useProjectRolesI18n';
import { MemberPicker } from '../../../ManageOrganizationPage/components/TeamsTab/MemberPicker';

const useStyles = makeStyles({
  listItemSecondaryAction: {
    visibility: 'hidden',
  },
  listItem: {
    '&:hover $listItemSecondaryAction': {
      visibility: 'inherit',
    },
  },
});

type Props = {
  organizationId: string;
  projectId: string;
};

export const MembersTab = memo<Props>(props => {
  const { t } = useTranslation();
  const classes = useStyles();

  const projectRoles = useProjectRolesI18n();

  const [isAddMemberPickerOpen, setIsAddMemberPickerOpen] = useState(false);
  const openAddMemberPicker = useCallback(() => setIsAddMemberPickerOpen(true), [setIsAddMemberPickerOpen]);
  const closeAddMemberPicker = useCallback(() => setIsAddMemberPickerOpen(false), [setIsAddMemberPickerOpen]);

  const organizationUsersQuery = useQuery(
    [fetchOrganizationUsers.key, { organizationId: props.organizationId }],
    fetchOrganizationUsers.fn,
  );
  const organizationUsers = useMemo(
    () =>
      (organizationUsersQuery.data?.payload || []).map(u => ({
        id: u.user.id,
        firstName: u.user.firstName,
        lastName: u.user.lastName,
        email: u.user.email,
      })),
    [organizationUsersQuery.data],
  );

  const teamsQuery = useQuery([fetchTeams.key, { organizationId: props.organizationId }], fetchTeams.fn);
  const teams = useMemo(() => teamsQuery.data?.payload || [], [teamsQuery.data]);

  const projectQuery = useQuery(
    [fetchProject.key, { organizationId: props.organizationId, projectId: props.projectId }],
    fetchProject.fn,
  );

  const projectMembers = useMemo(() => {
    const { users, teams } = projectQuery.data?.payload || { users: [], teams: [] };

    return [
      ...users.map(user => ({ type: 'user' as const, ...user })),
      ...teams.map(team => ({ type: 'team' as const, id: team.identifier, ...team })),
    ];
  }, [projectQuery.data]);

  const potentialProjectMembers = useMemo(() => {
    const users = organizationUsers.filter(user => !projectMembers.some(v => v.type === 'user' && v.id === user.id));
    const potentialTeams = teams.filter(
      team => !projectMembers.some(v => v.type === 'team' && v.identifier === team.identifier),
    );
    return { users, teams: potentialTeams };
  }, [organizationUsers, projectMembers, teams]);

  const addMembers = useCallback(
    async ({ userIds, teamIdentifiers }: { userIds: string[]; teamIdentifiers: string[] }) => {
      try {
        await ProjectUserApiService.add({ ...props, userIds, teamIdentifiers }).catch(notifyReportableApiError);
        closeAddMemberPicker();
      } catch (error) {
        notifyReportableApiError(error).catch(error => {
          console.log('Failed to notify reportable api:', error);
        });
      }
    },
    [closeAddMemberPicker, props],
  );

  const [memberToBeRemoved, setMemberToBeRemoved] = useState<(typeof projectMembers)[number] | null>(null);
  const clearMemberToBeRemoved = useCallback(() => setMemberToBeRemoved(null), [setMemberToBeRemoved]);
  const handleMemberRemoval = useCallback(async () => {
    if (memberToBeRemoved) {
      await ProjectUserApiService.remove({
        ...props,
        target:
          memberToBeRemoved.type === 'user'
            ? { userId: memberToBeRemoved.id }
            : { teamIdentifier: memberToBeRemoved.identifier },
      }).catch(notifyReportableApiError);
      clearMemberToBeRemoved();
    }
  }, [props, memberToBeRemoved, clearMemberToBeRemoved]);

  return (
    <QueryLoader queries={[projectQuery, organizationUsersQuery, teamsQuery]}>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gridColumn="1 / -1">
          <Typography variant="subtitle2">
            {t('organizationAdmin:teams.table.number_of_members', { count: projectMembers.length })}
          </Typography>

          <Button onClick={openAddMemberPicker} color="primary" data-cy="team-add-members-button">
            {t('organizationAdmin:teams.add_members')}
          </Button>
        </Stack>

        <Divider />

        <List>
          {!projectMembers.length ? (
            <ListItem key={'no members'} dense>
              <ListItemText primary={t('organizationAdmin:projects.management.members_list.no_members')} />
            </ListItem>
          ) : (
            projectMembers.map((item, i) => (
              <>
                <ListItem key={item.id} classes={{ container: classes.listItem }}>
                  <ListItemAvatar>
                    {item.type === 'user' ? (
                      <Avatar
                        src={UserApiService.getUserByIdThumbnailUrl({ userId: item.id })}
                        alt={`${item.firstName} ${item.lastName}`}
                      />
                    ) : (
                      <Avatar style={{ backgroundColor: CIColors.Tertiary }}>
                        <GroupIcon />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <Stack direction="row" width="100%">
                    <ListItemText
                      primary={item.type === 'user' ? `${item.firstName} ${item.lastName}` : item.displayName}
                      secondary={item.type === 'user' ? item.email : item.identifier}
                    />
                    <SelectWithDescription
                      disableUnderline
                      onSubmit={role =>
                        ProjectUserApiService.update({
                          ...props,
                          target: item.type === 'user' ? { userId: item.id } : { teamIdentifier: item.identifier },
                          role: role as ProjectRole,
                        }).catch(notifyReportableApiError)
                      }
                      value={item.role}
                      items={projectRoles}
                      typographyVariant="subtitle1"
                    />
                  </Stack>

                  <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
                    <IconButton
                      onClick={() => setMemberToBeRemoved(item)}
                      size="small"
                      data-cy={`team-remove-${
                        item.type === 'user' ? `${item.firstName}-${item.lastName}` : item.displayName
                      }`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {i + 1 !== projectMembers.length ? <Divider /> : null}
              </>
            ))
          )}
        </List>
        <MemberPicker
          users={potentialProjectMembers.users}
          teams={potentialProjectMembers.teams}
          open={isAddMemberPickerOpen}
          onCancel={closeAddMemberPicker}
          onSuccess={addMembers}
        />
        <SimpleConfirmModal
          open={!!memberToBeRemoved}
          message={
            <Typography>
              {t('organizationAdmin:teams.remove_member_part1')}
              <Typography component="span" style={{ fontWeight: 'bold' }}>
                {memberToBeRemoved?.type === 'user'
                  ? `${memberToBeRemoved?.firstName} ${memberToBeRemoved?.lastName} (${memberToBeRemoved?.email})`
                  : `${memberToBeRemoved?.displayName} (${memberToBeRemoved?.identifier})`}
              </Typography>
              {t('organizationAdmin:teams.remove_member_part2')}
            </Typography>
          }
          onCancel={clearMemberToBeRemoved}
          onConfirm={handleMemberRemoval}
        />
      </Stack>
    </QueryLoader>
  );
});
